import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const LEVEL_STYLES = {
  beginner: 'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300',
  intermediate: 'bg-accent-100 text-accent-800 dark:bg-accent-900/40 dark:text-accent-300',
  advanced: 'bg-stone-200 text-stone-700 dark:bg-stone-700 dark:text-stone-300',
};

function Lessons() {
  const { t, i18n } = useTranslation();
  const [lessons, setLessons] = useState([]);
  const [level, setLevel] = useState('');
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchLessons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level, i18n.language]);

  const fetchLessons = async () => {
    try {
      setLoading(true);
      const params = { language: i18n.language };
      if (level) params.level = level;

      const response = await axios.get(`${API_URL}/lessons`, { params });
      setLessons(response.data);
    } catch (error) {
      console.error('Failed to fetch lessons:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-10">
      <h1 className="font-display text-3xl font-semibold text-stone-800 dark:text-stone-100 mb-6">{t('lessons.title')}</h1>

      <div className="mb-8">
        <select
          value={level}
          onChange={(e) => setLevel(e.target.value)}
          className="px-4 py-2.5 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-xl text-stone-700 dark:text-stone-200 focus:outline-none focus:ring-2 focus:ring-brand-300 focus:border-brand-400"
        >
          <option value="">{t('lessons.selectCategory')}</option>
          <option value="beginner">{t('lessons.beginner')}</option>
          <option value="intermediate">{t('lessons.intermediate')}</option>
          <option value="advanced">{t('lessons.advanced')}</option>
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-16 text-stone-500 dark:text-stone-400">
          <Loader2 size={18} className="animate-spin" />
          Loading lessons...
        </div>
      ) : lessons.length === 0 ? (
        <div className="text-center py-16 text-stone-500 dark:text-stone-400">No lessons found</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {lessons.map((lesson) => (
            <Link
              key={lesson.id}
              to={`/lessons/${lesson.id}`}
              className="group bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 p-6 hover:border-brand-300 dark:hover:border-brand-700 hover:shadow-md transition-all flex flex-col"
            >
              <h3 className="font-display text-xl font-semibold text-stone-800 dark:text-stone-100 mb-2">{lesson.title}</h3>
              <p className="text-stone-500 dark:text-stone-400 text-sm mb-5 line-clamp-3 flex-1">{lesson.description}</p>
              <div className="flex gap-2 mb-5">
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${LEVEL_STYLES[lesson.level] || 'bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-300'}`}>
                  {lesson.level}
                </span>
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-300">
                  {lesson.category}
                </span>
              </div>
              <span className="mt-auto inline-flex items-center justify-center gap-1.5 w-full bg-brand-700 group-hover:bg-brand-800 text-white text-sm font-semibold py-2.5 rounded-xl transition-colors">
                {t('lessons.startLesson')}
                <ArrowRight size={15} />
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default Lessons;
